import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import Root from "./routes/root";
import Settings from "./components/settings/Settings";
import Billing from "./components/billing/Billing";
import Plugins from "./components/plugins/Plugins";
import ErrorPage from "./components/errorpage/ErrorPage";
import Workspaces from "./components/workspaces/Workspaces";
import Signup from "./components/signup/Signup";
import Login from "./components/login/Login";
import Logout from "./components/logout/Logout";
import { Auth0Provider } from "@auth0/auth0-react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import config from "environment";
import { Provider } from "react-redux";
import store from "./redux/store";
import {
  AskedQuestions,
  CollectionsAll,
  CollectionsFavorites,
  ManageCollections,
} from "./components/collections";
import { saveState } from "./utils/localStorage";
import throttle from "lodash/throttle";
import "react-tooltip/dist/react-tooltip.css";
import VerifyEmail from "./routes/VerifyEmail";
import SlackLanding from "./components/landing/SlackLanding";
import OAuthLanding from "./components/landing/OAuthLanding";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ExtensionLanding } from "./components/landing/ExtensionLanding";
import { Helmet } from "react-helmet";
import { CreateCollection } from "./components/collections/CreateCollection";
import { CollectionAsk } from "./components/collections/CollectionAsk";
import { CollectionResources } from "./components/collections/CollectionResources";
import { CollectionAskedQuestions } from "./components/collections/CollectionAskedQuestions";
import { CollectionStats } from "./components/stats/CollectionStats";
import { CollectionReportCard } from "./components/collection-report-card/CollectionReportCard";
import { SnippetManager } from "./components/snippet-manager/SnippetManager";
import { PublicCollections } from "./components/public-collections/PublicCollections";
import StripePricingTable from "./components/billing/StripePricingTable";
import DownloadDocument from "./components/collections/DownloadDocument";
import HomeScreenContainer from "./components/collections/HomeScreen/HomeScreenContainer";
import SlackAppInfo from "./components/SlackAppInfo";
import PersonaManager from "./components/personas/PersonaManager";
import PolicyStudio from "./components/policy-studio/PolicyStudio";
import PublicPolicyStudio from "./components/policy-studio/PublicPolicyStudio";
import { AgentChat } from "./components/agent-chat/AgentChat";
import { AppRoot } from "./components/app/app-root/AppRoot";
import { AppHome } from "./components/app/app-home/AppHome";
import { AppEmployeeRecords } from "./components/app/app-employee-records/AppEmployeeRecords";
import { AppCustomProperties } from "./components/app/app-customize/AppCustomProperties";
import { AppCustomPropertyEdit } from "./components/app/app-customize/AppCustomPropertyEdit";
import { AppEmployeeRecordsFlatten } from "./components/app/app-employee-records-flatten/AppEmployeeRecordsFlatten";
import { TemplateGenerator } from "./components/template-generator/TemplateGenerator";
import { Wazawan } from "./components/wazawan/Wazawan";
import { AppTimeOff } from "./components/app/app-time-off/AppTimeOff";
import { AppPTOPolicyEditor } from "./components/app/app-time-off/AppPTOPolicyEditor";
import { AppEmployeeTimeOff } from "./components/app/app-time-off/AppEmployeeTimeOff";
import { AppDocuments } from "./components/app/app-documents/AppDocuments";
import { AppTimeOffRequest } from "./components/app/app-time-off/AppTimeOffRequest";
import { AppCalendarPage } from "./components/app/app-calendar/AppCalendarPage";
import { AppPTOAdjustment } from "./components/app/app-time-off/AppPTOAdjustment";

const showBaton = config.showBaton;

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <HomeScreenContainer />,
      },
      {
        path: "/all",
        element: <CollectionsAll />,
      },
      {
        path: "/favorites",
        element: <CollectionsFavorites />,
      },
      {
        path: "/asked/:userId",
        element: <AskedQuestions />,
      },
      {
        path: "/manage",
        element: <ManageCollections />,
      },
      {
        path: "/home",
        element: <HomeScreenContainer />,
      },
      {
        path: "/create",
        element: <CreateCollection />,
      },
      {
        path: "/collection/:collectionId",
        element: <CollectionAsk />,
      },
      {
        path: "/collection/:collectionId/ask",
        element: <CollectionAsk />,
      },
      {
        path: "/collection/:collectionId/ask/:aiResponseSaveId",
        element: <CollectionAsk />,
      },
      {
        path: "/collection/:collectionId/resources",
        element: <CollectionResources />,
      },
      {
        path: "/collection/:collectionId/resources/:linkRecordId",
        element: <CollectionResources />,
      },
      {
        path: "/collection/:collectionId/asked",
        element: <CollectionAskedQuestions />,
      },
      {
        path: "/collection/:collectionId/stats",
        element: <CollectionStats />,
      },
      {
        path: "/collection/:collectionId/report-card",
        element: <CollectionReportCard />,
      },
      {
        path: "/snippets",
        element: <SnippetManager />,
      },
      {
        path: "/snippets/:snippetId",
        element: <SnippetManager />,
      },
      {
        path: "/personas",
        element: <PersonaManager />,
      },
      {
        path: "/workspaces",
        element: <Workspaces />,
      },
      {
        path: "/settings",
        element: <Settings />,
      },
      {
        path: "/billing",
        element: <Billing />,
      },
      {
        path: "/plugins",
        element: <Plugins />,
      },
      {
        path: "/slack/landing",
        element: <SlackLanding />,
      },
      {
        path: "/extension/landing",
        element: <ExtensionLanding />,
      },
      {
        path: "/oauth/landing",
        element: <OAuthLanding />,
      },
      {
        path: "/agent/:collectionId",
        element: <AgentChat />,
      },
    ],
  },
  {
    path: "/collection/:collectionId/document/:linkRecordId",
    element: <DownloadDocument />,
  },
  {
    path: "/slack-app-info",
    element: <SlackAppInfo />,
  },
  {
    path: "/signup",
    element: <Signup />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/logout",
    element: <Logout />,
  },
  {
    path: "/verify-email/:userId/:email/:token",
    element: <VerifyEmail />,
  },
  {
    path: "/pricing",
    element: <StripePricingTable />,
  },

  {
    path: "/public/public-collections",
    element: <PublicCollections />,
  },
  {
    path: "/public/public-collections/:publicCollectionId",
    element: <PublicCollections />,
  },
  {
    path: "/public/template-generator/:templateId",
    element: <TemplateGenerator />,
  },
  {
    path: "/policystudio",
    element: <PublicPolicyStudio />,
  },
  {
    path: "/ps",
    element: <PolicyStudio />,
  },
  // {
  //   path: "/waz",
  //   element: <AgentChat />,
  // },
  {
    path: "/waz",
    element: <Wazawan />,
  },
  {
    path: "/app",
    element: <AppRoot />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/app/home",
        element: <AppHome />,
      },
      {
        path: "/app/employee-records/",
        element: <AppEmployeeRecords />,
      },
      {
        path: "/app/employee-records/:employeeId",
        element: <AppEmployeeRecords />,
      },
      {
        path: "/app/time-off",
        element: <AppTimeOff />,
      },
      {
        path: "/app/time-off/policy-editor/:ptoPolicyId",
        element: <AppPTOPolicyEditor />,
      },
      {
        path: "/app/documents",
        element: <AppDocuments />,
      },
      {
        path: "/app/custom-properties/",
        element: <AppCustomProperties />,
      },
      { 
        path: "/app/custom-properties/update/:customPropertyId",
        element: <AppCustomPropertyEdit />,
      },
      {
        path: "/app/custom-properties/:customPropertyId",
        element: <AppCustomProperties />,
      },
      {
        path: "/app/employee-records/flatten",
        element: <AppEmployeeRecordsFlatten />,
      },
      {
        path: "/app/employee/time-off",
        element: <AppEmployeeTimeOff />,
      },
      {
        path: "/app/employee/time-off/request",
        element: <AppTimeOffRequest />,
      },
      {
        path: "/app/employee/time-off/request/:timeOffRequestId",
        element: <AppTimeOffRequest />,
      },
      {
        path: "/app/employee/time-off/adjust/:employeeId",
        element: <AppPTOAdjustment />,
      },
      {
        path: "/app/employee/calendar",
        element: <AppCalendarPage />,
      }
    ],
  },
  // {
  //   path: "/public/html/:page",
  //   element: <RedirectToStaticPage />
  // }
]);

const rootElement = document.getElementById("root");
const root = ReactDOM.createRoot(rootElement);

sessionStorage.setItem("signup", "false");
// sessionStorage.setItem("email", "");

store.subscribe(
  throttle(() => {
    saveState(store.getState());
  }),
  1000
);

root.render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOAUTH_CLIENT_ID}>
      <Auth0Provider
        // domain="dev-nw12vlxbhf5opc5z.us.auth0.com"
        domain="login.usewinslow.com"
        clientId="Dhv04GgqrrH9tt0iEZ6UF1SnzxNeuRnD"
        authorizationParams={{
          redirect_uri: config.redirect,
          audience: "https://dev-nw12vlxbhf5opc5z.us.auth0.com/api/v2/",
          prompt: "select_account",
        }}
        skipRedirectCallback={window.location.pathname === "/settings"}>
        <Provider store={store}>
          <Helmet>
            <title>Winslow</title>
            <meta property="og:title" content="Winslow" />
            <meta name="og:description" content="The simpler way to work." />
            <meta name="description" content="The simpler way to work." />
            <meta name="slack-app-id" content="A0623PANF34" />
          </Helmet>
          <DndProvider backend={HTML5Backend}>
            <RouterProvider router={router} />
          </DndProvider>
        </Provider>
      </Auth0Provider>
    </GoogleOAuthProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
